import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import AppDropdown from '../../../components/AppDropdown'
import { appToast } from '../../../components/AppToast'
import { useTestGroupListScreen } from './hooks/useTestGroupListScreen'

export default function TestGroupListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useTestGroupListScreen()
  const [searchField, setSearchField] = useState('Name')

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const handleTabClick = (tab) => {
    actions.setStatusFilter(tab)
    actions.setCurrentPage(1)
  }

  const handleEdit = (id) => {
    navigate(`/education/tests/groups/entry?id=${id}`)
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header (Rule 87: Canopy Hierarchy - No arrow_back) ── */}
      <section className="flex flex-col gap-4 border-b border-outline-variant/10 pb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
              Test Groups
            </h1>
            <p className="mt-1 text-xs font-medium text-on-surface-variant">
              Configure and manage examination series or groups for organizing assessments.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Status Tabs: Active | Archive | All */}
            <div className="flex items-center gap-1 rounded-2xl bg-surface-container-high p-1">
              {['Active', 'Archive', 'All'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleTabClick(tab)}
                  className={`rounded-xl px-4 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                    state.statusFilter === tab
                      ? 'bg-surface-container-lowest text-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Shared Filters (No tools dropdown per user request) ── */}
      <SharedFilters
        searchField={searchField}
        searchText={state.searchText}
        setSearchField={setSearchField}
        setSearchText={actions.setSearchText}
        onReset={actions.resetFilters}
        onSearch={() => actions.setCurrentPage(1)}
        searchFieldOptions={[{ value: 'Name', label: 'Group Name' }]}
        visibleAccordionFilters={[]}
        showAccordionToggle={false}
        showStatus={false}
      />

      {/* ── Groups Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm flex flex-col">
        <div className="grid grid-cols-[1fr_100px] items-center border-b border-outline-variant/25 bg-surface-container-low/60 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div>Group Name</div>
          <div className="text-right">Actions</div>
        </div>

        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading test groups...
            </div>
          ) : state.items.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <span className="material-symbols-outlined mb-3 block text-5xl text-on-surface-variant/40">folder_off</span>
              <p className="text-sm font-medium text-on-surface-variant">No test groups found for current filter.</p>
            </div>
          ) : (
            state.items.map((group, idx) => (
              <div
                key={group.id}
                className={`group grid grid-cols-[1fr_100px] items-center border-b border-outline-variant/10 px-8 py-4 transition-colors hover:bg-surface-container-low/40 last:border-b-0 cursor-pointer ${
                  idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                }`}
                onClick={() => handleEdit(group.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-xl text-primary/70">folder</span>
                  <span className="text-sm font-semibold text-on-surface group-hover:text-primary transition-colors">
                    {group.name}
                  </span>
                  {group.archive && (
                    <span className="rounded-md bg-outline-variant/20 px-2 py-0.5 text-[11px] font-bold text-on-surface-variant">
                      Archived
                    </span>
                  )}
                </div>

                <div className="flex justify-end select-none" onClick={(e) => e.stopPropagation()}>
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    menuClassName="min-w-[150px]"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      {
                        key: 'archive',
                        label: group.archive ? 'UnArchive' : 'Archive',
                        icon: group.archive ? 'unarchive' : 'archive',
                      },
                      { key: 'delete', label: 'Delete', icon: 'close', danger: true, iconClassName: 'text-error' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') handleEdit(group.id)
                      else if (key === 'archive') actions.handleToggleArchive(group)
                      else if (key === 'delete') actions.handleDelete(group.id, group.name)
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Pagination Footer ── */}
        <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-on-surface-variant">
            Showing {state.items.length === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1}–
            {Math.min(state.currentPage * state.pageSize, state.totalEntries)} of {state.totalEntries} entries
          </span>
          <div className="flex items-center gap-3">
            <div className="w-32">
              <AppDropdown
                value={state.pageSize}
                onChange={(val) => actions.setPageSize(Number(val))}
                options={[
                  { value: 10, label: '10 / page' },
                  { value: 25, label: '25 / page' },
                  { value: 50, label: '50 / page' },
                ]}
              />
            </div>
            {state.totalPages > 1 && (
              <AppPagination
                page={state.currentPage}
                totalPages={state.totalPages}
                canPrevious={state.currentPage > 1}
                canNext={state.currentPage < state.totalPages}
                onPrevious={() => actions.setCurrentPage(Math.max(1, state.currentPage - 1))}
                onNext={() => actions.setCurrentPage(Math.min(state.totalPages, state.currentPage + 1))}
                onPageChange={(p) => actions.setCurrentPage(p)}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
